/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 */

import {Component, inject, OnInit} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {
  IxActiveModal,
  ToastService,
  UploadFileState,
} from '@siemens/ix-angular';
import {TranslateService} from '@ngx-translate/core';
import {DppService} from '../../../../../services/dpp.service';
import {DocumentMetadata} from '../../../../../models/dpp/document-metadata';

const ALL_LANGUAGES: {label: string; value: string}[] = (() => {
  const displayNames = new Intl.DisplayNames(['en'], {type: 'language'});
  const tags = [
    'af',
    'sq',
    'am',
    'ar',
    'hy',
    'as',
    'az',
    'eu',
    'be',
    'bn',
    'bs',
    'bg',
    'my',
    'ca',
    'cs',
    'zh',
    'hr',
    'da',
    'nl',
    'en',
    'et',
    'fo',
    'fi',
    'fr',
    'gl',
    'ka',
    'de',
    'el',
    'gu',
    'he',
    'hi',
    'hu',
    'is',
    'id',
    'ga',
    'it',
    'ja',
    'kn',
    'kk',
    'km',
    'ko',
    'lo',
    'lv',
    'lt',
    'mk',
    'ms',
    'ml',
    'mt',
    'mr',
    'mn',
    'ne',
    'nb',
    'nn',
    'or',
    'ps',
    'fa',
    'pl',
    'pt',
    'pa',
    'ro',
    'ru',
    'sr',
    'sk',
    'sl',
    'es',
    'sw',
    'sv',
    'ta',
    'te',
    'th',
    'tr',
    'uk',
    'ur',
    'uz',
    'vi',
    'cy',
    'zu',
  ];
  return tags
    .map((tag) => ({label: displayNames.of(tag) ?? tag, value: tag}))
    .sort((a, b) => a.label.localeCompare(b.label));
})();

@Component({
  selector: 'app-modal-add-document',
  templateUrl: './modal-add-document.component.html',
  styleUrls: ['./modal-add-document.component.scss'],
  standalone: false,
})
export class ModalAddDocumentComponent implements OnInit {
  protected readonly activeModal = inject(IxActiveModal);
  private readonly formBuilder = inject(FormBuilder);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly dppService = inject(DppService);

  readonly allLanguages = ALL_LANGUAGES;

  documentForm: FormGroup;
  selectedFile?: File;
  isSubmitting = false;
  uploadState = UploadFileState.SELECT_FILE;
  uploadErrorMessage = '';

  categories = [
    {
      labelKey: 'dpp.handover_documentation.modal.categories.identification',
      value: 'identification',
    },
    {
      labelKey:
        'dpp.handover_documentation.modal.categories.technical_specification',
      value: 'technical_specification',
    },
    {
      labelKey: 'dpp.handover_documentation.modal.categories.drawings_plans',
      value: 'drawings_plans',
    },
    {
      labelKey: 'dpp.handover_documentation.modal.categories.assemblies',
      value: 'assemblies',
    },
    {
      labelKey:
        'dpp.handover_documentation.modal.categories.certificates_declarations',
      value: 'certificates_declarations',
    },
    {
      labelKey:
        'dpp.handover_documentation.modal.categories.commissioning_decommissioning',
      value: 'commissioning_decommissioning',
    },
    {
      labelKey: 'dpp.handover_documentation.modal.categories.operation',
      value: 'operation',
    },
    {
      labelKey: 'dpp.handover_documentation.modal.categories.general_safety',
      value: 'general_safety',
    },
    {
      labelKey:
        'dpp.handover_documentation.modal.categories.inspection_maintenance_testing',
      value: 'inspection_maintenance_testing',
    },
    {
      labelKey: 'dpp.handover_documentation.modal.categories.repair',
      value: 'repair',
    },
    {
      labelKey: 'dpp.handover_documentation.modal.categories.spare_parts',
      value: 'spare_parts',
    },
    {
      labelKey:
        'dpp.handover_documentation.modal.categories.contract_documents',
      value: 'contract_documents',
    },
  ];

  ngOnInit(): void {
    this.documentForm = this.formBuilder.group({
      languageTitles: this.formBuilder.array([this.createLanguageTitleGroup()]),
      category: ['', Validators.required],
      language: ['', Validators.required],
      date: ['', Validators.required],
      version: ['', Validators.required],
    });
  }

  get languageTitles(): FormArray {
    return this.documentForm.get('languageTitles') as FormArray;
  }

  createLanguageTitleGroup(): FormGroup {
    return this.formBuilder.group({
      documentLanguage: ['', Validators.required],
      documentTitle: ['', Validators.required],
    });
  }

  addLanguageTitle(): void {
    this.languageTitles.push(this.createLanguageTitleGroup());
  }

  removeLanguageTitle(index: number): void {
    if (this.languageTitles.length > 1) {
      this.languageTitles.removeAt(index);
    }
  }

  getAvailableLanguagesForRow(index: number): {label: string; value: string}[] {
    const usedCodes = this.languageTitles.controls
      .map((ctrl, i) =>
        i === index ? null : ctrl.get('documentLanguage')?.value,
      )
      .filter(Boolean) as string[];
    return this.allLanguages.filter((l) => !usedCodes.includes(l.value));
  }

  isAddButtonDisabled(): boolean {
    return this.isSubmitting || !this.selectedFile || this.documentForm.invalid;
  }

  onFilesChanged(event: CustomEvent<File[]>): void {
    this.uploadErrorMessage = '';
    this.uploadState = UploadFileState.SELECT_FILE;
    const file = event.detail.at(0);
    if (file) {
      this.selectedFile = file;
    }
  }

  async onSubmit(): Promise<void> {
    if (this.isSubmitting || !this.selectedFile) {
      return;
    }

    this.isSubmitting = true;
    const formValue = this.documentForm.value;

    try {
      const submodelId: string | undefined =
        this.activeModal.data?.['submodelId'];
      if (!submodelId) {
        throw new Error('Missing submodelId in modal data');
      }

      const documentMetadata: DocumentMetadata = {
        title: formValue.languageTitles.map(
          (entry: {documentLanguage: string; documentTitle: string}) => ({
            language: entry.documentLanguage,
            text: entry.documentTitle,
          }),
        ),
        category: {
          classId: formValue.category,
          className: [{language: formValue.language, text: formValue.category}],
        },
        language: formValue.language,
        date: new Date(formValue.date).toISOString(),
        version: formValue.version,
      };

      this.uploadState = UploadFileState.LOADING;
      await this.dppService.addSubmodelAttachment(
        submodelId,
        documentMetadata,
        this.selectedFile,
      );
      this.uploadState = UploadFileState.UPLOAD_SUCCESSED;

      this.toastService.show({
        message: this.translateService.instant(
          'dpp.handover_documentation.document_added_success',
        ),
        type: 'success',
      });

      this.activeModal.close({
        success: true,
        document: {
          languageTitles: formValue.languageTitles,
          category: formValue.category,
          language: formValue.language,
          date: formValue.date,
          version: formValue.version,
          file: this.selectedFile,
        },
      });
    } catch {
      this.uploadState = UploadFileState.UPLOAD_FAILED;
      this.toastService.show({
        message: this.translateService.instant(
          'dpp.handover_documentation.document_added_error',
        ),
        type: 'error',
      });
    } finally {
      this.isSubmitting = false;
    }
  }

  onCancel(): void {
    this.activeModal.dismiss('dismiss');
  }
}
